// Tall obstacles (FAA DOF) layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.
//
// Uses the LayerFamilySpec shape introduced in batch 9.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { ensureObstacleIcons, OBSTACLE_ICON_EXPRESSION } from '../obstacleIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

/**
 * Mount the obstacles source + its layers. Idempotent on the source.
 */
export function addObstaclesLayers(map: MlMap): void {
    if (!map.getSource('obstacles')) {
      map.addSource('obstacles', { type: 'geojson', data: EMPTY_FC });
      // Kick off SVG icon registration; the layer paints once images are
      // registered (MapLibre redraws on image-load).
      void ensureObstacleIcons(map);
      map.addLayer({
        id: 'obstacles-icon',
        type: 'symbol',
        source: 'obstacles',
        minzoom: 8,
        layout: {
          visibility: 'none',
          'icon-image': OBSTACLE_ICON_EXPRESSION,
          // Bumped 2026-05-14 per Aaron field report: yellow mast was
          // blending into the VFR sectional's tan background. Combined
          // with the SVG redesign (dark mast + white halo + magenta
          // flag on tall) the icon now reads clearly at every zoom.
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            8, 0.65,
            10, 0.85,
            13, 1.10,
          ],
          'icon-anchor': 'bottom',  // base of the mast = lat/lon
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'obstacles-label',
        type: 'symbol',
        source: 'obstacles',
        minzoom: 10,
        layout: {
          visibility: 'none',
          // AGL height in ft. Tall obstacles (>=1000 AGL) also include
          // the type for context.
          'text-field': [
            'case',
            ['>=', ['coalesce', ['get', 'agl'], 0], 1000],
            [
              'format',
              ['concat', ['to-string', ['coalesce', ['get', 'agl'], 0]], '′'],
              {},
              '\n',
              {},
              ['get', 'type'],
              { 'font-scale': 0.75 },
            ],
            ['concat', ['to-string', ['coalesce', ['get', 'agl'], 0]], '′'],
          ],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            10, 9, 12, 11, 14, 12,
          ],
          'text-anchor': 'top',
          'text-offset': [0, 1.2],
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 2,
        },
        paint: {
          'text-color': '#fef3c7',
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.4,
        },
      });
    }
}

registerLayerFamily({
  id: 'obstacles',
  label: 'Tall obstacles (FAA DOF)',
  sourceId: 'obstacles',
  layerIds: ['obstacles-icon', 'obstacles-label'],
  mount: (map) => addObstaclesLayers(map),
  visibilitySelector: (l) => l.obstacles,
  applyState: (map) => {
    if (!map.getLayer('obstacles-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.obstacles;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('obstacles-icon', 'visibility', v);
    if (map.getLayer('obstacles-label')) {
      map.setLayoutProperty('obstacles-label', 'visibility', v);
    }
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('obstacles');
    if (!spec) return;
    markOverlayLoaded('obstacles', true);
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!useLayerVisibilityStore.getState().layers.obstacles) return;
    const spec = OVERLAY_BY_ID.get('obstacles');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
